import React from "react";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField,
    Typography,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import Tab from "@mui/material/Tab";
import TabPanel from "@mui/lab/TabPanel";
import {DatePicker} from "@mui/x-date-pickers";
import {parseISO} from "date-fns";
import {formatIsoToHHMM} from "../../common/DateUtil";
import ServiceCard from "../common/ServiceCard";

// Extracted dialog for AddAppointment flow
export default function AddAppointmentDialog({
                                                 open,
                                                 onClose,
                                                 fullScreen,
                                                 isXs,
                                                 t,
                                                 isEditMode,
                                                 currentAnimal,
                                                 activeTab,
                                                 setActiveTab,
                                                 canGoToStep2,
                                                 newAppointmentDto,
                                                 toggleAnimalForAppointments,
                                                 handleSelectChange,
                                                 servicesLoading,
                                                 filteredServices,
                                                 onSelectService,
                                                 loading,
                                                 error,
                                                 openAppointmentSchedules,
                                                 selectedAppointments,
                                                 toggleAppointment,
                                                 animals,
                                                 checkAvailability,
                                                 handleSaveAppointment,
                                             }) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>
                <Typography color="secondary" variant={isXs ? "h6" : "h5"}>
                    {isEditMode ? t("editAppointment") : t("addAppointment")}
                </Typography>
                {currentAnimal?.person && (
                    <Typography variant="subtitle2" sx={{mt: 0.5}}>
                        {`${currentAnimal.person.forename ?? ""} ${currentAnimal.person.name ?? ""}`}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent dividers>
                <TabContext value={activeTab}>
                    <Box sx={{borderBottom: 1, borderColor: "divider"}}>
                        <TabList
                            onChange={(_, v) => setActiveTab(v)}
                            aria-label="reservation steps tabs"
                            data-testid="appointment-dialog-tabs"
                        >
                            <Tab label={t("updateDates")} value="1" data-testid="tab-details" />
                            <Tab label={t("selectStay")} value="2" disabled={!canGoToStep2} data-testid="tab-select-slot" />
                        </TabList>
                    </Box>

                    <TabPanel value="1" sx={{px: 0}}>
                        <Box sx={{display: "grid", gap: 2}}>
                            <Box>
                                <Typography variant="subtitle1" sx={{mb: 1}}>
                                    {t("selectCats")}
                                </Typography>
                                <Box sx={{display: "flex", flexWrap: "wrap", gap: 2}}>
                                    {(currentAnimal?.animals ?? []).map((animal) => (
                                        <FormControlLabel
                                            key={animal.id}
                                            control={
                                                <Checkbox
                                                    disabled={isEditMode}
                                                    checked={newAppointmentDto.animalsInReservationIds.includes(
                                                        animal.id,
                                                    )}
                                                    onChange={() =>
                                                        toggleAnimalForAppointments(animal.id)
                                                    }
                                                    color="primary"
                                                />
                                            }
                                            label={animal.name}
                                        />
                                    ))}
                                </Box>
                            </Box>

                            <Box sx={{display: "grid", gap: 1}}>
                                <DatePicker
                                    label={t("appointmentDate")}
                                    value={
                                        newAppointmentDto.startAt
                                            ? (typeof newAppointmentDto.startAt === 'string' ? parseISO(newAppointmentDto.startAt) : newAppointmentDto.startAt)
                                            : null
                                    }
                                    onChange={(v) => handleSelectChange(v, "startAt")}
                                    renderInput={(params) => <TextField {...params} fullWidth/>}
                                />
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                mt: 2,
                                // When there are more than 2 services, make the list scrollable
                                maxHeight: (filteredServices?.length || 0) > 2 ? 300 : "none",
                                overflowY:
                                    (filteredServices?.length || 0) > 2 ? "auto" : "visible",
                                pr: (filteredServices?.length || 0) > 2 ? 1 : 0,
                            }}
                        >
                            {!servicesLoading &&
                                (filteredServices || []).map((s) => (
                                    <ServiceCard
                                        key={s.uuid}
                                        service={s}
                                        onSelect={onSelectService}
                                        selected={newAppointmentDto?.service?.uuid === s.uuid}
                                        showBuffers={true}
                                    />
                                ))}
                        </Box>
                    </TabPanel>

                    <TabPanel value="2" sx={{px: 0}}>
                        {loading && <Typography>{t("loadingTimeslots")}</Typography>}
                        {!!error && <Typography color="error">{error}</Typography>}
                        {!loading && openAppointmentSchedules.length === 0 && (
                            <Typography color="warning.main">
                                {t("noTimeslotsSuggestion")}
                            </Typography>
                        )}
                        {!loading && openAppointmentSchedules.length > 0 && (
                            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                                {/**
                                 * animalsInReservationIds contains only animal IDs (backend payload expectation).
                                 * Resolve each id to the corresponding animal object for UI rendering and stable keys.
                                 */}
                                {newAppointmentDto.animalsInReservationIds
                                    .map((id, idx) => {
                                        const found =
                                            (animals || []).find((a) => a.id === id) ||
                                            (currentAnimal.animals || []).find((a) => a.id === id) ||
                                            {};
                                        return {
                                            id: id,
                                            animalName: found.animalName || found.name || undefined,
                                            index: idx,
                                        };
                                    })
                                    .map((animalRow) => {
                                        const animalKey = String(
                                            animalRow.id ?? `idx-${animalRow.index}`,
                                        );
                                        return (
                                            <Box
                                                key={`row-${animalKey}`}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 2,
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                {/** Animal label (if available) */}
                                                {animalRow.animalName && (
                                                    <Typography variant="subtitle2" sx={{mr: 1}}>
                                                        {animalRow.animalName}
                                                    </Typography>
                                                )}
                                                <Box sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
                                                    {openAppointmentSchedules.map((openAppointment) => {
                                                        const isSelectedForRow = selectedAppointments.some(
                                                            (value) =>
                                                                value.uuid === openAppointment.uuid &&
                                                                value._animalKey === animalKey,
                                                        );
                                                        const isTakenByOtherRow = selectedAppointments.some(
                                                            (value) =>
                                                                value.uuid === openAppointment.uuid &&
                                                                value._animalKey !== animalKey,
                                                        );
                                                        return (
                                                            <Button
                                                                key={`${animalKey}-${openAppointment.startAt}`}
                                                                variant={
                                                                    isSelectedForRow ? "contained" : "outlined"
                                                                }
                                                                disabled={isTakenByOtherRow}
                                                                onClick={() =>
                                                                    toggleAppointment(
                                                                        openAppointment,
                                                                        animalKey,
                                                                        {
                                                                            id: animalRow.id,
                                                                            animalName: animalRow.animalName,
                                                                            index: animalRow.index,
                                                                        },
                                                                    )
                                                                }
                                                            >
                                                                {formatIsoToHHMM(openAppointment.startAt)}
                                                            </Button>
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        );
                                    })}
                            </Box>
                        )}
                    </TabPanel>
                </TabContext>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} data-testid="cancel-appointment-button">{t("cancel")}</Button>
                {activeTab === "1" && (
                    <Button
                        onClick={checkAvailability}
                        disabled={
                            !newAppointmentDto.startAt ||
                            newAppointmentDto.animalsInReservationIds.length < 1 ||
                            !newAppointmentDto?.service?.uuid
                        }
                        data-testid="check-availability-button"
                    >
                        {t("checkAvailability")}
                    </Button>
                )}
                <Button
                    onClick={handleSaveAppointment}
                    disabled={
                        !(
                            newAppointmentDto.animalsInReservationIds.length > 0 &&
                            newAppointmentDto.startAt &&
                            newAppointmentDto.service &&
                            selectedAppointments.length ===
                            newAppointmentDto.animalsInReservationIds.length
                        )
                    }
                    data-testid="save-appointment-button"
                >
                    {t("save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
